import React, { useEffect, useState, useCallback } from 'react';
import { ref, set, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaEdit } from 'react-icons/fa';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';

const KvaPriceManager = ({ selectedCompany, selectedDivision }) => {
  const normalizedCompany = normalizeCompanyKey(selectedCompany);
  const companyPath = firebaseCompanyKey(normalizedCompany);
  const [kvaPrices, setKvaPrices] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editedPrice, setEditedPrice] = useState('');
  const [newType, setNewType] = useState('CRGO');
  const [newKva, setNewKva] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const fetchKvaPrices = useCallback(async () => {
    if (!normalizedCompany || !selectedDivision) {
      console.error('Missing company or division:', { normalizedCompany, selectedDivision });
      alert('Company or division not specified.');
      return;
    }

    const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
    const divisionPath = `companies/${sanitizedCompany}/divisions`;
    const kvaPricesPath = `companies/${sanitizedCompany}/${selectedDivision}/kvaPrices`;

    console.log('Fetching KVA prices from path:', kvaPricesPath);

    try {
      // Check if division exists, initialize if not
      const divisionRef = ref(realtimeDb, divisionPath);
      const divisionSnapshot = await get(divisionRef);
      let divisions = divisionSnapshot.exists() ? divisionSnapshot.val() : [];
      if (!Array.isArray(divisions)) {
        divisions = divisions ? Object.values(divisions) : [];
      }
      if (!divisions.includes(selectedDivision)) {
        console.log(`Adding new division "${selectedDivision}" for company "${normalizedCompany}"`);
        await set(ref(realtimeDb, `${divisionPath}/${selectedDivision}`), true);
        console.log(`Division "${selectedDivision}" initialized successfully.`);

        // Initialize default KVA prices for new division
        const defaultKvaPrices = {
          "100 KVA AMDT": 15000,
          "100 KVA CRGO": 12000,
          "65 KVA AMDT": 10000,
          "65 KVA CRGO": 8000
        };
        await set(ref(realtimeDb, kvaPricesPath), defaultKvaPrices);
        console.log(`Default KVA prices set for division "${selectedDivision}"`);
        setKvaPrices(defaultKvaPrices);
      } else {
        // Fetch existing KVA prices
        const snapshot = await get(ref(realtimeDb, kvaPricesPath));
        const data = snapshot.exists() ? snapshot.val() : {};
        console.log('KVA prices data:', data);
        setKvaPrices(data);
      }
    } catch (error) {
      console.error('Error fetching or initializing KVA prices:', error);
      alert(`Failed to load or initialize KVA prices: ${error.message}`);
    }
  }, [normalizedCompany, selectedDivision]);

  useEffect(() => {
    fetchKvaPrices();
  }, [fetchKvaPrices]);

  const savePrices = async (updated) => {
    const path = `companies/${companyPath}/${selectedDivision}/kvaPrices`;
    console.log('Saving KVA prices to path:', path, updated);
    try {
      await set(ref(realtimeDb, path), updated);
      console.log('KVA prices saved successfully');
    } catch (error) {
      console.error('Error saving KVA prices:', error);
      alert(`Failed to save KVA prices: ${error.message}`);
    }
  };

  const handleEdit = (key) => {
    setEditingKey(key);
    setEditedPrice(kvaPrices[key]);
  };

  const handleSave = async (key) => {
    const parsedPrice = parseFloat(editedPrice);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      alert('Please enter a valid price.');
      return;
    }
    const updated = { ...kvaPrices, [key]: parsedPrice };
    setKvaPrices(updated);
    setEditingKey(null);
    await savePrices(updated);
  };

  const handleAdd = async () => {
    const key = `${newKva} KVA ${newType}`.trim();
    const parsedPrice = parseFloat(newPrice);
    if (!newKva || !newPrice || isNaN(parsedPrice) || parsedPrice < 0 || kvaPrices[key]) {
      alert('Please enter a valid KVA and price, and ensure the KVA type does not already exist.');
      return;
    }
    const updated = { ...kvaPrices, [key]: parsedPrice };
    setKvaPrices(updated);
    setNewKva('');
    setNewPrice('');
    setNewType('CRGO');
    await savePrices(updated);
  };

  return (
    <div className="p-4 bg-white rounded shadow">
      <h3 className="text-xl font-semibold mb-4">Manage KVA Pricing – {normalizedCompany} ({selectedDivision})</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['CRGO', 'AMDT'].map((type) => (
          <div key={type}>
            <h4 className="text-lg font-bold mb-2">{type} Prices</h4>
            <table className="w-full border text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-3 py-1">KVA</th>
                  <th className="border px-3 py-1">Price (₹)</th>
                  <th className="border px-3 py-1">Action</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kvaPrices)
                  .filter(([k]) => k.includes(type))
                  .map(([key, value]) => (
                    <tr key={key}>
                      <td className="border px-3 py-1">{key.replace(` ${type}`, '')}</td>
                      <td className="border px-3 py-1">
                        {editingKey === key ? (
                          <input
                            type="number"
                            value={editedPrice}
                            onChange={(e) => setEditedPrice(e.target.value)}
                            className="border px-2 py-1 w-24"
                          />
                        ) : (
                          `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        )}
                      </td>
                      <td className="border px-3 py-1">
                        {editingKey === key ? (
                          <button onClick={() => handleSave(key)} className="text-green-600">
                            Save
                          </button>
                        ) : (
                          <button onClick={() => handleEdit(key)} className="text-blue-600">
                            <FaEdit />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="mt-6 border-t pt-4">
        <h5 className="mb-2 font-semibold">Add New KVA Price</h5>
        <div className="flex flex-wrap gap-2">
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            className="border px-2 py-1"
          >
            <option value="CRGO">CRGO</option>
            <option value="AMDT">AMDT</option>
          </select>
          <input
            placeholder="KVA"
            value={newKva}
            onChange={(e) => setNewKva(e.target.value)}
            className="border px-2 py-1 w-20"
          />
          <input
            placeholder="Price"
            type="number"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            className="border px-2 py-1 w-24"
          />
          <button onClick={handleAdd} className="bg-green-600 text-white px-3 py-1 rounded">
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default KvaPriceManager;